import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import {
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Search,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Activity,
  Clock,
  User,
  Loader2,
  FileText,
  MessageSquare,
  Eye,
  Download,
  X,
  Zap,
  ExternalLink,
  AlertCircle,
  Link2,
  Calendar,
  CalendarCheck,
  ClipboardList,
  Braces,
  Info,
} from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { Breadcrumb } from "../components/Breadcrumb";
import { ModalShell } from "../components/Modal";
import { useToastStore } from "../../stores/toastStore";
import { FollowUpChatModal } from "../components/FollowUpChatModal";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dropdown } from "../components/Dropdown";
import {
  getAssessmentPlan,
  getDeclarativeResults,
  getLinkedEvidence,
  previewEvidenceFile,
  downloadSupportingEvidenceFile,
  getAssessmentResultByPlan,
  compileResults,
  getCompiledResults,
  getExecutionSummary,
  listPoams,
  getPoamItemsByPlan,
  getAssessmentExecutionView,
  type ControlExecutionStatusResponse,
  type MappedOpaControlResult,
  type PendingControlStatement,
  type ExecutionReviewItem,
  type EvidenceLink,
  type CompiledResult,
  type ExecutionSummary,
  type ApiPoamItem,
  type AssessmentExecutionViewResponse,
  type AssessmentExecutionViewControl,
  type AssessmentExecutionViewStatement,
} from "../../api/frameworksApi";

/* ── Helpers ── */

const STATUS_KEYWORDS = new Set(["follow_up_raised", "follow_up_resolved", "accepted", "approved", "pending", "draft"]);

function countVisibleChatMessages(history: import("../../api/frameworksApi").ReviewHistoryEntry[]): number {
  let count = 0;
  for (const entry of history) {
    if (entry.followUpQuestion) count++;
    if (entry.followUpResponse && !STATUS_KEYWORDS.has(entry.followUpResponse)) count++;
  }
  return count;
}

function parseStatementId(stmtId: string): {
  controlId: string;
  label: string;
} {
  const parts = stmtId.split("_smt.");
  const controlId = (parts[0] ?? stmtId).toUpperCase().replace(/-/g, "-");
  const label = stmtId;
  return { controlId, label };
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-GB") +
    " " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
}

/** Format an ISO timestamp in UTC, matching the raw value stored by the API. */
function formatDateUtc(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return (
    d.toLocaleDateString("en-GB", { timeZone: "UTC" }) +
    " " +
    d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    })
  );
}

function shortId(uuid: string): string {
  return uuid.slice(0, 8);
}

const reviewStatusConfig: Record<
  string,
  { label: string; dot: string; bg: string; text: string; ring: string }
> = {
  pending: {
    label: "Awaiting Review",
    dot: "bg-amber-400",
    bg: "bg-amber-50 dark:bg-amber-500/10",
    text: "text-amber-700 dark:text-amber-400",
    ring: "ring-amber-200 dark:ring-amber-500/20",
  },
  accepted: {
    label: "Accepted",
    dot: "bg-emerald-500",
    bg: "bg-emerald-50 dark:bg-emerald-500/10",
    text: "text-emerald-700 dark:text-emerald-400",
    ring: "ring-emerald-200 dark:ring-emerald-500/20",
  },
  follow_up_raised: {
    label: "Follow-Up Required",
    dot: "bg-orange-500",
    bg: "bg-orange-50 dark:bg-orange-500/10",
    text: "text-orange-700 dark:text-orange-400",
    ring: "ring-orange-200 dark:ring-orange-500/20",
  },
  follow_up_resolved: {
    label: "Resolved",
    dot: "bg-teal-500",
    bg: "bg-teal-50 dark:bg-teal-500/10",
    text: "text-teal-700 dark:text-teal-400",
    ring: "ring-teal-200 dark:ring-teal-500/20",
  },
};

type MethodFilter =
  | "all"
  | "declarative"
  | "declarative_evidence"
  | "automation";
type ResultFilter = "all" | "pass" | "fail";

/* ── Component ── */

export function AssessmentResultDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [decData, setDecData] = useState<ExecutionReviewItem[]>([]);
  const [decLoading, setDecLoading] = useState(false);
  const [decError, setDecError] = useState<string | null>(null);

  const [execView, setExecView] = useState<AssessmentExecutionViewResponse | null>(null);
  const [execViewLoading, setExecViewLoading] = useState(true);

  const [methodFilter, setMethodFilter] = useState<MethodFilter>("all");
  const [resultFilter, setResultFilter] = useState<ResultFilter>("all");

  type SortKey = "index" | "controlId" | "description" | "method" | "status";
  type SortDir = "asc" | "desc";
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
    setPage(0);
  };

  const [evidenceMap, setEvidenceMap] = useState<
    Record<string, EvidenceLink[]>
  >({});

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const [jsonModal, setJsonModal] = useState<{
    techControlId: string;
    status: string;
    hostname?: string;
    ipAddress?: string;
    label: string;
    field: string;
    content: string;
  } | null>(null);

  const [arId, setArId] = useState<string | null>(null);
  const [compiledResults, setCompiledResults] = useState<CompiledResult[]>([]);
  const [isCompiled, setIsCompiled] = useState(false);
  const [isCompiling, setIsCompiling] = useState(false);
  const [compileConfirm, setCompileConfirm] = useState(false);
  const [compileSuccess, setCompileSuccess] = useState(false);
  const [execSummary, setExecSummary] = useState<ExecutionSummary | null>(null);
  const [poamItems, setPoamItems] = useState<ApiPoamItem[]>([]);
  const [poamId, setPoamId] = useState<string | null>(null);
  const [expandedCompiled, setExpandedCompiled] = useState<Set<string>>(
    new Set(),
  );
  const [planControls, setPlanControls] = useState<
    Record<string, { description: string; statements: Record<string, string> }>
  >({});
  const [planInfo, setPlanInfo] = useState<{ title?: string; sspTitle?: string; importSspHref?: string; startDate?: string; endDate?: string; owningOrgUnitId?: string } | null>(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [violationPages, setViolationPages] = useState<Record<string, number>>(
    {},
  );
  const [evidencePages, setEvidencePages] = useState<Record<string, number>>({});
  const [opaCheckPages, setOpaCheckPages] = useState<Record<string, number>>({});
  const [checkFilter, setCheckFilter] = useState<"all" | "pass" | "fail">("all");
  const [componentFilter, setComponentFilter] = useState("");
  const [checksPerPage, setChecksPerPage] = useState(10);
  const [checkSearch, setCheckSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterOpen, setFilterOpen] = useState(false);
  const [drawerItem, setDrawerItem] = useState<{
    kind: string;
    data: any;
    rowKey: string;
    meta: any;
  } | null>(null);


  useEffect(() => {
    setCheckSearch("");
    setCheckFilter("all");
    setComponentFilter("");
    setOpaCheckPages({});
  }, [drawerItem?.rowKey]);

  const [chatOpen, setChatOpen] = useState(false);
  const [chatItem, setChatItem] = useState<ExecutionReviewItem | null>(null);

  const toggleExpand = (key: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const getViolationPage = (key: string): number => violationPages[key] ?? 0;
  const setViolationPage = (key: string, page: number) => {
    setViolationPages((prev) => ({ ...prev, [key]: page }));
  };

  const controlStatuses = useMemo<ControlExecutionStatusResponse[]>(() => {
    const normTechStatus = (s?: string) => {
      const v = (s ?? "").toLowerCase();
      if (v.includes("pass")) return "passed";
      if (v.includes("fail")) return "failed";
      if (v.includes("pending")) return "pending";
      return v || "pending";
    };
   
    const parseEvidence = (raw?: string): Record<string, unknown>[] => {
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        return [{ raw }];
      }
    };
   
    const dedupeByCheckAsset = (results: MappedOpaControlResult[]) => {
      const seen = new Set<string>();
      return results.filter((opa) => {
        const key = `${opa.opaControlId}_${opa.assetUuid ?? ""}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };
    return (execView?.controls ?? []).map((c) => {
      const controlType: "automated" | "manual" =
        (c.mode ?? "").toUpperCase() === "AUTOMATED" ? "automated" : "manual";

      
      const opaResults: MappedOpaControlResult[] = dedupeByCheckAsset(
        (c.statements ?? [])
          .flatMap((s) => s.assets ?? [])
          .flatMap((asset) =>
            (asset.controls ?? []).map((tech) => ({
              opaControlId: tech.tech_control_id,
              catalogName: null,
              controlStatus: normTechStatus(tech.status),
              evidence: parseEvidence(tech.evidence_data),
              assetUuid: asset.asset_uuid ?? null,
              assetName: asset.asset_hostname ?? null,
              assetIdentifierType: null,
            })),
          ),
      );
      const opaOverallStatus: "passed" | "failed" | null =
        opaResults.length === 0
          ? null
          : opaResults.some((o) => o.controlStatus === "failed")
            ? "failed"
            : "passed";

      const statements: PendingControlStatement[] = (c.statements ?? []).map((s) => ({
        id: s.statement_id,
        statementId: s.statement_label || s.statement_id,
        statementDescription: s.statement_description ?? "",
        assessmentMethods: s.assessment_methods ?? [],
        sortOrder: 0,
        createdAt: "",
        updatedAt: "",
        executionMethod: s.execution_method ?? null,
        state: s.state,
        justification: s.justification ?? null,
      }));

      return {
        planControlId: c.plan_control_id,
        controlId: c.control_id,
        controlDescription: c.control_description ?? null,
        statements,
        sortOrder: 0,
        implementationStatus: null,
        controlType,
        executable: controlType === "automated",
        executed: true,
        followUpPending: false,
        followUpQuestion: null,
        opaOverallStatus,
        opaResults,
        determination: null,
        justification: null,
        executedAt: null,
        executionViewStatements: c.statements ?? [],
        executionViewAssets: (c.statements ?? []).flatMap((s) => s.assets ?? []),
      } satisfies ControlExecutionStatusResponse;
    });
  }, [execView]);

  const fetchDeclarative = useCallback(async () => {
    if (!id) return;
    setDecLoading(true);
    setDecError(null);
    try {
      const [res, evRes] = await Promise.all([
        getDeclarativeResults(id, "assessment_result"),
        getLinkedEvidence(id, "assessment_result").catch(() => ({ data: [] as EvidenceLink[] })),
      ]);
      setDecData(res.data ?? []);
      const map: Record<string, EvidenceLink[]> = {};
      for (const link of evRes.data ?? []) {
        if (!map[link.statementId]) map[link.statementId] = [];
        map[link.statementId].push(link);
      }
      setEvidenceMap(map);
    } catch (err: any) {
      setDecError(err?.message || "Failed to load declarative results");
    } finally {
      setDecLoading(false);
    }
  }, [id]);

  const fetchExecView = useCallback(async () => {
    if (!id) return;
    setExecViewLoading(true);
    try {
      const res = await getAssessmentExecutionView(id);
      setExecView(res.data ?? null);
    } catch {
      setExecView(null);
    } finally {
      setExecViewLoading(false);
    }
  }, [id]);

  const hasEvidence = (statementId: string) =>
    (evidenceMap[statementId]?.length ?? 0) > 0;

  const handleEvidencePreview = async (evidenceId: string, title: string) => {
    setPreviewTitle(title);
    setPreviewLoading(true);
    try {
      const { url: blobUrl } = await previewEvidenceFile(evidenceId);
      setPreviewUrl(blobUrl);
    } catch (err) {
      console.error("Preview failed:", err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewTitle("");
  };

  /* ── Compilation: initial load ── */
  const fetchCompilationData = useCallback(async () => {
    if (!id) return;
    try {
      const [arRes, planRes, sumRes] = await Promise.all([
        getAssessmentResultByPlan(id),
        getAssessmentPlan(id).catch(() => ({ data: null })),
        getExecutionSummary(id).catch(() => ({ data: null })),
      ]);

      const fetchedArId = arRes.data?.id ?? null;
      setArId(fetchedArId);

      const ctrlMap: Record<
        string,
        { description: string; statements: Record<string, string> }
      > = {};
      for (const rc of (planRes.data as any)?.reviewedControls ?? []) {
        const stmtMap: Record<string, string> = {};
        for (const s of rc.statements ?? []) {
          stmtMap[s.statementId] = s.statementDescription ?? "";
        }
        ctrlMap[rc.controlId] = {
          description: rc.controlDescription ?? "",
          statements: stmtMap,
        };
      }
      setPlanControls(ctrlMap);

      const planData = planRes.data as any;
      if (planData) {
        setPlanInfo({
          title: planData.title,
          sspTitle: planData.sspTitle,
          importSspHref: planData.importSspHref,
          startDate: planData.task?.startDate ?? planData.startDate,
          endDate: planData.task?.endDate ?? planData.endDate,
          owningOrgUnitId: planData.owningOrgUnitId,
        });
      }

      setExecSummary(sumRes.data as ExecutionSummary | null);

      if (fetchedArId) {
        const compRes = await getCompiledResults(fetchedArId).catch(() => ({
          data: [] as CompiledResult[],
        }));
        const results = compRes.data ?? [];
        setCompiledResults(results);
        setIsCompiled(results.length > 0);

        if (results.length > 0 && id) {
          const poamsRes = await listPoams().catch(() => ({
            data: [] as any[],
          }));
          const poams = poamsRes.data ?? [];
          if (poams.length > 0) {
            const pId = poams[0].id;
            setPoamId(pId);
            const itemsRes = await getPoamItemsByPlan(pId, id).catch(() => ({
              data: [] as ApiPoamItem[],
            }));
            setPoamItems(itemsRes.data ?? []);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch compilation data:", err);
    }
  }, [id]);

  /* ── Compile handler ── */
  const handleCompile = async () => {
    if (!arId) return;
    setCompileConfirm(false);
    setIsCompiling(true);
    try {
      const res = await compileResults(arId, undefined, planInfo?.owningOrgUnitId);
      const results = res.data ?? [];
      setCompiledResults(results);
      setIsCompiled(results.length > 0);

      if (id) {
        const sumRes = await getExecutionSummary(id).catch(() => ({
          data: null,
        }));
        setExecSummary(sumRes.data as ExecutionSummary | null);
      }

      if (id) {
        const poamsRes = await listPoams().catch(() => ({ data: [] as any[] }));
        const poams = poamsRes.data ?? [];
        if (poams.length > 0) {
          const pId = poams[0].id;
          setPoamId(pId);
          const itemsRes = await getPoamItemsByPlan(pId, id).catch(() => ({
            data: [] as ApiPoamItem[],
          }));
          setPoamItems(itemsRes.data ?? []);
        }
      }
      setCompileSuccess(true);
    } catch (err) {
      console.error("Compilation failed:", err);
      useToastStore.getState().showError("Error", "Compilation failed. Please try again.");
    } finally {
      setIsCompiling(false);
    }
  };

  useEffect(() => {
    setPage(0);
    setSearch("");
    setExpandedIds(new Set());
    setMethodFilter("all");
    setResultFilter("all");
    setExpandedCompiled(new Set());
    setCheckFilter("all");
    setComponentFilter("");
    setCheckSearch("");
    setViolationPages({});
    setEvidencePages({});
    setOpaCheckPages({});
    setDrawerItem(null);
    setPreviewUrl(null);
    setJsonModal(null);
    setCompileSuccess(false);
    fetchDeclarative();
    fetchExecView();
  }, [id, fetchDeclarative, fetchExecView]);

  useEffect(() => {
    fetchCompilationData();
  }, [fetchCompilationData]);

  useEffect(() => {
    if (decError) useToastStore.getState().showError("Error", decError);
  }, [decError]);

  useEffect(() => {
    setPage(0);
  }, [methodFilter, resultFilter, search]);

  
  const execModeByControlId = new Map<string, string>(
    (execView?.controls ?? []).map((c) => [c.control_id, (c.mode ?? "").toUpperCase()]),
  );
  const isExecManualControl = (controlId: string) =>
    execModeByControlId.get(controlId) === "MANUAL";

 
  const autoStatuses = controlStatuses.filter(
    (s) =>
      s.controlType === 'automated' &&
      (s.opaResults?.length ?? 0) > 0 &&
      !isExecManualControl(s.controlId),
  );
  const autoControlIdSet = new Set(autoStatuses.map((s) => s.controlId));

  const manualDeterminationMap = new Map<string, boolean | null>(
    controlStatuses
      .filter((s) => s.controlType === 'manual')
      .map((s) => [s.controlId, s.determination]),
  );
  const resolveDecDetermination = (controlId: string, fallback: boolean | null | undefined): boolean =>
    manualDeterminationMap.has(controlId)
      ? (manualDeterminationMap.get(controlId) ?? false)
      : (fallback ?? false);

  const execControlByControlId = new Map<string, AssessmentExecutionViewControl>(
    (execView?.controls ?? []).map((c) => [c.control_id, c]),
  );
  const isAcceptedReview = (rs?: string | null) =>
    rs === "accepted" || rs === "approved";
  /** All statements accepted → SATISFIED; any rejected/non-accepted → NOT_SATISFIED. */
  const controlStatusFromExecView = (
    control: AssessmentExecutionViewControl,
  ): "SATISFIED" | "NOT_SATISFIED" => {
    const statements = control.statements ?? [];
    const allAccepted = statements.every((s) => isAcceptedReview(s.review_status));
    return allAccepted ? "SATISFIED" : "NOT_SATISFIED";
  };
  const execStatusByControlId = new Map<string, "SATISFIED" | "NOT_SATISFIED">(
    (execView?.controls ?? []).map((c) => [c.control_id, controlStatusFromExecView(c)]),
  );
  /** review_status-based satisfied for a control, or null when execution-view has no data. */
  const isControlSatisfiedById = (controlId: string): boolean | null => {
    const s = execStatusByControlId.get(controlId);
    return s == null ? null : s === "SATISFIED";
  };
  /** Satisfied resolver for a declarative item — prefers review_status, falls back to determination. */
  const decItemSatisfied = (item: ExecutionReviewItem): boolean => {
    const cid = item.execution?.controlId ?? "";
    const rs = isControlSatisfiedById(cid);
    return rs != null ? rs : resolveDecDetermination(cid, item.execution?.determination);
  };
  /** Satisfied resolver for an automated control — prefers review_status, falls back to OPA verdict. */
  const autoItemSatisfied = (c: ControlExecutionStatusResponse): boolean => {
    const rs = isControlSatisfiedById(c.controlId);
    return rs != null ? rs : c.opaOverallStatus === "passed";
  };
  /** Lookup a statement's execution-view record by its label or UUID. */
  const execStatementByKey = new Map<string, AssessmentExecutionViewStatement>();
  for (const c of execView?.controls ?? []) {
    for (const s of c.statements ?? []) {
      if (s.statement_label) execStatementByKey.set(s.statement_label, s);
      if (s.statement_id) execStatementByKey.set(s.statement_id, s);
    }
  }
  const filteredDecItems = decData.filter((d) => {
    const exec = d.execution;
    if (!exec) return false;

    if (exec.executionMethod === "opa" && !isExecManualControl(exec.controlId)) return false;
    if (autoControlIdSet.has(exec.controlId)) return false;

    if (
      methodFilter === "declarative" &&
      (evidenceMap[exec.statementId]?.length ?? 0) > 0
    )
      return false;
    if (
      methodFilter === "declarative_evidence" &&
      (evidenceMap[exec.statementId]?.length ?? 0) === 0
    )
      return false;
    if (methodFilter === "automation") return false;

    const det = decItemSatisfied(d);
    if (resultFilter === "pass" && !det) return false;
    if (resultFilter === "fail" && det) return false;

    if (search) {
      const q = search.toLowerCase();
      const { controlId, label } = parseStatementId(exec.statementId);
      return (
        controlId.toLowerCase().includes(q) || label.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const isTerminalReviewStatus = (status?: string | null) =>
    status === "accepted" || status === "approved" || status === "rejected";

  const allDecFinalized =
    decData.length > 0 &&
    decData.every((item) => isTerminalReviewStatus(item.currentReviewStatus));

  const reviewComplete = execView?.review_complete;
  const gateBlocked = reviewComplete === false;
  const resultsReady =
    reviewComplete === true
      ? true
      : reviewComplete === false
        ? false
        : decData.length > 0
          ? allDecFinalized
          : autoStatuses.length > 0;


  const extraAutoItems: ControlExecutionStatusResponse[] = (() => {
    const existing = new Set(autoStatuses.map((s) => s.controlId));
    const extra = new Map<string, ControlExecutionStatusResponse>();
    for (const d of decData) {
      const exec = d.execution;
      if (!exec || exec.executionMethod !== "opa") continue;

      if (isExecManualControl(exec.controlId)) continue;
      if (existing.has(exec.controlId) || extra.has(exec.controlId)) continue;
      extra.set(exec.controlId, {
        controlId: exec.controlId,
        controlDescription: exec.controlDescription ?? "",
        controlType: "automated",
        determination: exec.determination ?? null,
        opaOverallStatus: exec.determination ? "passed" : "failed",
        opaResults: [],
      } as ControlExecutionStatusResponse);
    }
    return Array.from(extra.values());
  })();
  const allAutoStatuses = [...autoStatuses, ...extraAutoItems];

  const visibleDec =
    methodFilter === "automation"
      ? []
      : decData.filter((d) => {
          if (!d.execution) return false;
          if (d.execution.executionMethod === "opa" && !isExecManualControl(d.execution.controlId)) return false;
          if (autoControlIdSet.has(d.execution.controlId)) return false;
          if (methodFilter === "declarative")
            return (evidenceMap[d.execution.statementId]?.length ?? 0) === 0;
          if (methodFilter === "declarative_evidence")
            return (evidenceMap[d.execution.statementId]?.length ?? 0) > 0;
          return true;
        });
  const visibleAuto =
    methodFilter === "all" || methodFilter === "automation" ? allAutoStatuses : [];
  const tabAll = visibleDec.length + visibleAuto.length;
  const tabPass =
    visibleDec.filter((d) => decItemSatisfied(d)).length +
    visibleAuto.filter((c) => autoItemSatisfied(c)).length;
  const tabFail = tabAll - tabPass;

  /* ── Filtered automation items ── */
  const filteredAutoItems = allAutoStatuses.filter((c) => {
    if (
      methodFilter === "declarative" ||
      methodFilter === "declarative_evidence"
    )
      return false;
    const passed = autoItemSatisfied(c);
    if (resultFilter === "pass" && !passed) return false;
    if (resultFilter === "fail" && passed) return false;
    if (search) {
      const q = search.toLowerCase();
      const matchesControl =
        c.controlId.toLowerCase().includes(q) ||
        (c.controlDescription ?? "").toLowerCase().includes(q);
      const matchesOpa = (c.opaResults ?? []).some(
        (r) =>
          r.opaControlId.toLowerCase().includes(q) ||
          (r.catalogName ?? "").toLowerCase().includes(q),
      );
      return matchesControl || matchesOpa;
    }
    return true;
  });

  type DecGroup = {
    controlId: string;
    controlDescription: string;
    items: ExecutionReviewItem[];
  };
  const decGrouped: DecGroup[] = (() => {
    const map = new Map<string, DecGroup>();
    for (const d of filteredDecItems) {
      const exec = d.execution;
      const cId =
        exec.controlId || parseStatementId(exec.statementId).controlId;
      if (!map.has(cId)) {
        map.set(cId, {
          controlId: cId,
          controlDescription: exec.controlDescription || "",
          items: [],
        });
      }
      map.get(cId)!.items.push(d);
    }
    return Array.from(map.values());
  })();

  type UnifiedItem =
    | { kind: "declarative_group"; data: DecGroup }
    | { kind: "automation"; data: ControlExecutionStatusResponse };
  const unifiedItems: UnifiedItem[] = [
    ...decGrouped.map((g) => ({ kind: "declarative_group" as const, data: g })),
    ...filteredAutoItems.map((a) => ({ kind: "automation" as const, data: a })),
  ];


  const isLoading = decLoading || execViewLoading;
  const currentError = decError;
  const retry = () => {
    fetchDeclarative();
    fetchExecView();
  };

  /* ── Compiled results grouped by control ── */
  const compiledByControl = (() => {
    const map = new Map<
      string,
      { controlId: string; statements: CompiledResult[]; satisfied: boolean }
    >();
    for (const r of compiledResults) {
      if (!map.has(r.controlId)) {
        map.set(r.controlId, {
          controlId: r.controlId,
          statements: [],
          satisfied: true,
        });
      }
      const group = map.get(r.controlId)!;
      group.statements.push(r);
      if (r.finalDetermination !== "satisfied" && r.finalDetermination !== "not-applicable")
        group.satisfied = false;
    }
    return Array.from(map.values());
  })();

  const compiledSatisfied = compiledByControl.filter((c) => c.satisfied).length;
  const compiledFailed = compiledByControl.filter((c) => !c.satisfied).length;
  const failedPoamItems = poamItems.filter(
    (i) => (i.relatedRisks?.length ?? 0) > 0,
  );

  const donutTotal = 0;

  const getItemMeta = (item: UnifiedItem) => {
    if (item.kind === "automation") {
      const ctrl = item.data as ControlExecutionStatusResponse;
      const checks = ctrl.opaResults?.length ?? 0;
      const noData =
        isControlSatisfiedById(ctrl.controlId ?? "") == null &&
        ctrl.opaOverallStatus == null &&
        checks === 0;
      return {
        method: "Automation" as const,
        checks,
        passed: autoItemSatisfied(ctrl),
        noData,
        description: ctrl.controlDescription ?? "",
        controlId: ctrl.controlId ?? "",
      };
    }
    const group = item.data as DecGroup;
    const allPassed = group.items.every((i) => decItemSatisfied(i));
    const hasEv = group.items.some((i) =>
      hasEvidence(i.execution?.statementId ?? ""),
    );
    return {
      method: (hasEv ? "Declarative + Evidence" : "Declarative") as string,
      checks: group.items.length,
      passed: allPassed,
      noData: false,
      description: group.controlDescription ?? "",
      controlId: group.controlId,
    };
  };

  /** Reviewer's final accept/reject decision (+ note, iteration) for a statement. */
  const renderReviewerDecision = (stmt?: AssessmentExecutionViewStatement) => {
    if (!stmt || stmt.review_status == null) return null;
    const accepted = isAcceptedReview(stmt.review_status);
    return (
      <div
        className={`rounded-xl border p-4 ${
          accepted
            ? "bg-emerald-50/60 dark:bg-emerald-500/[0.06] border-emerald-200 dark:border-emerald-500/15"
            : "bg-red-50/60 dark:bg-red-500/[0.06] border-red-200 dark:border-red-500/15"
        }`}
      >
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-[12px] font-semibold text-[#334155] dark:text-white/85">
            Reviewer Decision
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-bold ${
            accepted
              ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
              : "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400"
          }`}
        >
          {accepted ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />}
          {accepted ? "Accepted" : "Rejected"}
        </span>
        {stmt.note && (
          <div className="mt-2.5">
            <p className="text-[11px] font-semibold text-[#334155] dark:text-white/85 mb-1">
              Reviewer note
            </p>
            <p className="text-[13px] text-[#0f172a] dark:text-white/90 leading-relaxed">
              {stmt.note}
            </p>
          </div>
        )}
      </div>
    );
  };

  const fmtMaybeDate = (s?: string | null) => {
    if (!s) return "";
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : formatDate(s);
  };
  const normalizeTechStatus = (s?: string) => {
    const v = (s ?? "").toLowerCase();
    if (v.includes("pass")) return "passed";
    if (v.includes("fail")) return "failed";
    if (v.includes("pending")) return "pending";
    return v || "pending";
  };

  /** Frozen OPA snapshot the reviewer approved: assets[] × tech-controls matrix. */
  const renderAssetMatrix = (stmt: AssessmentExecutionViewStatement) => {
    const assets = stmt.assets ?? [];
    if (assets.length === 0) return null;
    const allChecks = assets.flatMap((asset) =>
      (asset.controls ?? []).map((tech) => ({ asset, tech })),
    );
    const componentOptions = Array.from(
      new Set(
        allChecks
          .map((c) => c.asset.component_name?.trim())
          .filter((name): name is string => !!name),
      ),
    ).sort((a, b) => a.localeCompare(b));
    const componentMatched = componentFilter
      ? allChecks.filter((c) => c.asset.component_name?.trim() === componentFilter)
      : allChecks;

    const q = checkSearch.trim().toLowerCase();
    const searchMatched = !q
      ? componentMatched
      : componentMatched.filter((c) => {
          const fields = [
            c.asset.asset_hostname,
            c.asset.ip_address,
            c.asset.component_name,
            c.tech.tech_control_id,
            c.tech.tech_control_description,
            normalizeTechStatus(c.tech.status),
          ];
          return fields.some((f) => (f ?? "").toString().toLowerCase().includes(q));
        });
    const passCount = searchMatched.filter((c) => normalizeTechStatus(c.tech.status) === "passed").length;
    const failCount = searchMatched.filter((c) => normalizeTechStatus(c.tech.status) === "failed").length;
    const flatChecks = searchMatched.filter((c) => {
      if (checkFilter === "all") return true;
      const s = normalizeTechStatus(c.tech.status);
      return checkFilter === "pass" ? s === "passed" : s === "failed";
    });
    const pageKey = stmt.statement_id;
    const curPage = opaCheckPages[pageKey] ?? 0;
    const totalCheckPages = Math.ceil(flatChecks.length / checksPerPage);
    const pagedChecks = flatChecks.slice(
      curPage * checksPerPage,
      (curPage + 1) * checksPerPage,
    );
    const setCheckPage = (updater: (p: number) => number) =>
      setOpaCheckPages((prev) => ({ ...prev, [pageKey]: updater(prev[pageKey] ?? 0) }));
    const filterTabs: { key: "all" | "pass" | "fail"; label: string; count: number; active: string }[] = [
      { key: "all", label: "All", count: searchMatched.length, active: "bg-indigo-600 dark:bg-violet-600 text-white shadow-sm" },
      { key: "pass", label: "Passed", count: passCount, active: "bg-emerald-600 text-white shadow-sm" },
      { key: "fail", label: "Failed", count: failCount, active: "bg-red-600 text-white shadow-sm" },
    ];
    return (
      <div className="space-y-3">
        {/* Trendy passed/failed filter + rows-per-page — sticky so it stays visible while scrolling long check lists.
            The `before:` pseudo extends an opaque 24px cap above the bar so any scrolled-content gap from the
            scroll container's padding-top (py-5) is hidden, not just translucent. */}
        <div className="sticky -top-[20px] z-50 -mx-6 px-6 pt-[20px] pb-3 bg-white dark:bg-[#131320] border-b border-[#e2e8f0] dark:border-white/[0.06] shadow-[0_6px_12px_-6px_rgba(0,0,0,0.15)] dark:shadow-[0_6px_12px_-6px_rgba(0,0,0,0.6)] flex items-center justify-between gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-[280px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-[#64748b] dark:text-white/50 pointer-events-none" />
            <input
              type="text"
              value={checkSearch}
              onChange={(e) => { setCheckSearch(e.target.value); setCheckPage(() => 0); }}
              placeholder="Search hostname, IP, status"
              className="w-full h-[34px] pl-8 pr-7 text-[12px] bg-white dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.08] rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/30 dark:focus:ring-violet-500/30 focus:border-indigo-500 dark:focus:border-violet-500/50 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/40 text-[#0f172a] dark:text-white"
            />
            {checkSearch && (
              <button
                type="button"
                onClick={() => { setCheckSearch(""); setCheckPage(() => 0); }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-[#f1f5f9] dark:hover:bg-white/[0.08] transition-colors"
                aria-label="Clear search"
              >
                <X className="size-3 text-[#64748b] dark:text-white/60" />
              </button>
            )}
          </div>
          <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-[#f1f5f9] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06]">
            {filterTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => { setCheckFilter(t.key); setCheckPage(() => 0); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[12px] font-bold transition-all ${
                  checkFilter === t.key
                    ? t.active
                    : "text-[#475569] dark:text-white/70 hover:bg-white dark:hover:bg-white/[0.06]"
                }`}
              >
                {t.label}
                <span className={`inline-flex items-center justify-center min-w-[24px] h-[20px] px-1.5 rounded-full text-[11px] font-bold ${
                  checkFilter === t.key ? "bg-white/30 text-white" : "bg-[#cbd5e1] dark:bg-white/20 text-[#0f172a] dark:text-white"
                }`}>
                  {t.count}
                </span>
              </button>
            ))}
          </div>
          <Dropdown
            value={componentFilter}
            onChange={(v) => { setComponentFilter(v); setCheckPage(() => 0); }}
            options={[
              { value: "", label: "All components" },
              ...componentOptions.map((name) => ({ value: name, label: name })),
            ]}
            placeholder="Choose component"
            size="sm"
            align="start"
            className="!h-[32px] !min-w-[150px] !max-w-[180px] !px-2.5 text-[12px] shadow-[0_8px_24px_-18px_rgba(124,58,237,0.85)]"
          />
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold text-[#0f172a] dark:text-white/90">Rows per page</span>
            <Dropdown
              value={String(checksPerPage)}
              onChange={(v) => { setChecksPerPage(Number(v)); setCheckPage(() => 0); }}
              options={[10, 25, 100].map((n) => ({ value: String(n), label: String(n) }))}
              size="sm"
              align="end"
              className="!min-w-[80px]"
            />
          </div>
        </div>
        {flatChecks.length === 0 && (
          <p className="text-[12px] text-[#334155] dark:text-white/85 py-2">
            {checkSearch
              ? `No checks match "${checkSearch}".`
              : componentFilter
                ? `No checks found for ${componentFilter}.`
              : `No ${checkFilter === "pass" ? "passed" : "failed"} checks.`}
          </p>
        )}
        {pagedChecks.map(({ asset, tech }, i) => {
          const st = normalizeTechStatus(tech.status);
          const ok = st === "passed";
          const failed = st === "failed";
          return (
            <div
              key={`${asset.asset_uuid}-${tech.tech_control_id}-${i}`}
              className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden"
            >
              <div className="px-4 py-2.5 bg-[#f8fafc] dark:bg-white/[0.02] border-b border-[#e2e8f0] dark:border-white/[0.06] flex items-center gap-2 flex-wrap text-[11px] text-[#1e293b] dark:text-white/85">
                <span className="font-mono text-[12px] font-bold text-[#0f172a] dark:text-white">
                  {asset.asset_hostname}
                </span>
                {asset.ip_address && <span>({asset.ip_address})</span>}
                {asset.component_name && (
                  <span>
                    · Component:{" "}
                    <span className="font-semibold text-[#0f172a] dark:text-white">
                      {asset.component_name}
                    </span>
                  </span>
                )}
                {asset.scanned_at && (
                  <span className="flex items-center gap-1">
                    <Calendar className="size-3" />
                    Scanned {fmtMaybeDate(asset.scanned_at)}
                  </span>
                )}
              </div>
              <div className="px-4 py-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="font-mono text-[12px] font-bold text-indigo-600 dark:text-violet-400">
                      {tech.tech_control_id?.toUpperCase()}
                    </span>
                    {tech.tech_control_description && (
                      <p className="text-[12px] text-[#1e293b] dark:text-white/90 mt-0.5 leading-snug">
                        {tech.tech_control_description}
                      </p>
                    )}
                  </div>
                  <span
                    className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold ${
                      ok
                        ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                        : failed
                          ? "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400"
                          : "bg-slate-100 dark:bg-white/[0.06] text-slate-600 dark:text-white/40"
                    }`}
                  >
                    {ok ? <CheckCircle2 className="size-3" /> : failed ? <XCircle className="size-3" /> : <Clock className="size-3" />}
                    {st}
                  </span>
                </div>
                {(tech.command || tech.evidence_data) && (
                  <div className="flex items-center gap-4 pt-0.5">
                    {tech.command && (
                      <button
                        type="button"
                        onClick={() =>
                          setJsonModal({
                            techControlId: tech.tech_control_id,
                            status: st,
                            hostname: asset.asset_hostname,
                            ipAddress: asset.ip_address ?? undefined,
                            label: "Command",
                            field: "command",
                            content: tech.command ?? "",
                          })
                        }
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 dark:text-violet-300 hover:text-indigo-700 dark:hover:text-violet-200 transition-colors"
                      >
                        <Braces className="size-3" />
                        Command
                      </button>
                    )}
                    {tech.evidence_data && (
                      <button
                        type="button"
                        onClick={() =>
                          setJsonModal({
                            techControlId: tech.tech_control_id,
                            status: st,
                            hostname: asset.asset_hostname,
                            ipAddress: asset.ip_address ?? undefined,
                            label: "Evidence",
                            field: "evidence_data",
                            content: tech.evidence_data ?? "",
                          })
                        }
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 dark:text-violet-300 hover:text-indigo-700 dark:hover:text-violet-200 transition-colors"
                      >
                        <Braces className="size-3" />
                        Evidence
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {totalCheckPages > 1 && (
          <div className="flex items-center justify-between pt-1">
            <button
              onClick={() => setCheckPage((p) => Math.max(0, p - 1))}
              disabled={curPage === 0}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-[#1e293b] dark:text-white/90 bg-[#f1f5f9] dark:bg-white/[0.06] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.10] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="size-3.5" /> Previous
            </button>
            <span className="text-[12px] font-medium text-[#334155] dark:text-white/85">
              Page {curPage + 1} of {totalCheckPages}
            </span>
            <button
              onClick={() => setCheckPage((p) => Math.min(totalCheckPages - 1, p + 1))}
              disabled={curPage >= totalCheckPages - 1}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-[#1e293b] dark:text-white/90 bg-[#f1f5f9] dark:bg-white/[0.06] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.10] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRight className="size-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const currentItems = sortKey
    ? [...unifiedItems].sort((a, b) => {
        if (sortKey === "index") {
          const ai = unifiedItems.indexOf(a), bi = unifiedItems.indexOf(b);
          return sortDir === "asc" ? ai - bi : bi - ai;
        }
        const ma = getItemMeta(a);
        const mb = getItemMeta(b);
        let av = "", bv = "";
        if (sortKey === "controlId") { av = ma.controlId; bv = mb.controlId; }
        else if (sortKey === "description") { av = ma.description; bv = mb.description; }
        else if (sortKey === "method") { av = ma.method; bv = mb.method; }
        else if (sortKey === "status") { av = ma.passed ? "satisfied" : "not satisfied"; bv = mb.passed ? "satisfied" : "not satisfied"; }
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      })
    : unifiedItems;
  const totalPages = Math.ceil(currentItems.length / rowsPerPage);
  const pagedItems = currentItems.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage,
  );
  const formatPlanDate = (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString("en-GB")
      : null;
  const planStart = formatPlanDate(planInfo?.startDate);
  const planEnd = formatPlanDate(planInfo?.endDate);
  const sspName =
    planInfo?.sspTitle || planInfo?.importSspHref?.split("/").pop() || null;
  const hasPlanInfo =
    !!planInfo && (!!planInfo.title || !!sspName || !!planStart || !!planEnd);
  const showOverviewSummary = false;
  const showOverviewCard = hasPlanInfo || showOverviewSummary;
  const planInfoCards = [
    {
      label: "Plan Name",
      value: planInfo?.title || "-",
      icon: ClipboardList,
      color: "text-indigo-500 dark:text-indigo-400",
      bg: "bg-indigo-50 dark:bg-indigo-500/10",
    },
    {
      label: "Linked SSP",
      value: sspName || "-",
      icon: Link2,
      color: "text-sky-500 dark:text-sky-400",
      bg: "bg-sky-50 dark:bg-sky-500/10",
    },
    {
      label: "Start Date",
      value: planStart || "-",
      icon: Calendar,
      color: "text-emerald-500 dark:text-emerald-400",
      bg: "bg-emerald-50 dark:bg-emerald-500/10",
    },
    {
      label: "End Date",
      value: planEnd || "-",
      icon: CalendarCheck,
      color: "text-amber-500 dark:text-amber-400",
      bg: "bg-amber-50 dark:bg-amber-500/10",
    },
  ];

  return (
    <PageShell className="space-y-6">
      <Breadcrumb
        parent="Assessment Results"
        parentTo="/assessment-results"
        current={planInfo ? (planInfo.title || "Assessment Results") : <span className="inline-block h-4 w-48 max-w-full animate-pulse rounded bg-foreground/10 align-middle" />}
      />

      {showOverviewCard && hasPlanInfo && (
        <div className="space-y-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Info className="size-3.5 text-indigo-500 dark:text-indigo-400" />
            <h2 className="text-[14px] font-medium text-[#0f172a] dark:text-white">
              Plan Information
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {planInfoCards.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.label}
                  className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] px-4 py-3 flex items-center gap-3 min-w-0"
                >
                  <div className={`size-9 rounded-lg flex items-center justify-center flex-shrink-0 ${c.bg}`}>
                    <Icon className={`size-4 ${c.color}`} />
                  </div>
                  <div className="min-w-0 flex-1 group/info-tip relative">
                    <p className="text-[11px] font-medium text-[#94a3b8] dark:text-white/60">
                      {c.label}
                    </p>
                    <p
                      className="text-[13px] font-semibold text-[#0f172a] dark:text-white truncate cursor-default"
                    >
                      {c.value}
                    </p>
                    {c.value !== "-" && (
                      <div className="pointer-events-none absolute left-0 top-full z-[80] mt-2 hidden max-w-[420px] rounded-xl border border-[#e2e8f0] bg-white px-3.5 py-2.5 text-xs font-semibold text-[#0f172a] shadow-xl shadow-black/10 ring-1 ring-black/5 group-hover/info-tip:block dark:border-white/10 dark:bg-[#0b1020] dark:text-white dark:shadow-black/40 dark:ring-violet-500/20">
                        <div className="mb-1 text-[10px] font-bold tracking-wider text-violet-600 dark:text-violet-300/80">
                          {c.label}
                        </div>
                        <div className="whitespace-normal leading-relaxed">
                          {c.value}
                        </div>
                        <span className="absolute -top-1.5 left-5 size-3 rotate-45 border-l border-t border-[#e2e8f0] bg-white dark:border-white/10 dark:bg-[#0b1020]" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showOverviewCard && showOverviewSummary && (() => {
        const pct = 0;
        const pctColor = "text-[#64748b] dark:text-white/50";
        return (
          <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] px-5 py-4 space-y-3">
            {/* Plan info + stats row */}
            <div className="flex items-center gap-x-5 gap-y-2 text-[13px]">
              {hasPlanInfo && (
                <>
                  <span className="flex items-center gap-1.5 group/plan relative flex-shrink-0">
                    <span className="text-[#64748b] dark:text-white/70 font-medium">Plan:</span>
                    <span className="font-bold text-[#0f172a] dark:text-white truncate max-w-[160px] cursor-default">
                      {planInfo?.title || '-'}
                    </span>
                    {planInfo?.title && (
                      <span className="absolute left-0 top-full mt-1.5 z-50 hidden group-hover/plan:block px-3 py-2 rounded-lg bg-[#0f172a] dark:bg-[#0f1420] text-[12px] text-white font-medium shadow-xl border border-white/10 whitespace-nowrap max-w-[400px]">
                        {planInfo.title}
                        <span className="absolute -top-1 left-4 w-2 h-2 rotate-45 bg-[#0f172a] dark:bg-[#0f1420] border-l border-t border-white/10" />
                      </span>
                    )}
                  </span>
                  <span className="text-[#e2e8f0] dark:text-white/10 flex-shrink-0">|</span>
                  <span className="flex items-center gap-1.5 group/ssp relative flex-shrink-0">
                    <span className="text-[#64748b] dark:text-white/70 font-medium">SSP:</span>
                    <span className="font-bold text-[#0f172a] dark:text-white truncate max-w-[180px] cursor-default">
                      {sspName || '-'}
                    </span>
                    {sspName && (
                      <span className="absolute left-0 top-full mt-1.5 z-50 hidden group-hover/ssp:block px-3 py-2 rounded-lg bg-[#0f172a] dark:bg-[#0f1420] text-[12px] text-white font-medium shadow-xl border border-white/10 whitespace-nowrap max-w-[400px]">
                        {sspName}
                        <span className="absolute -top-1 left-4 w-2 h-2 rotate-45 bg-[#0f172a] dark:bg-[#0f1420] border-l border-t border-white/10" />
                      </span>
                    )}
                  </span>
                  <span className="text-[#e2e8f0] dark:text-white/10 flex-shrink-0">|</span>
                  <span className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[#64748b] dark:text-white/70 font-medium">Period:</span>
                    <span className="font-bold text-[#0f172a] dark:text-white whitespace-nowrap">
                      {planStart || '-'} → {planEnd || '-'}
                    </span>
                  </span>
                </>
              )}
              {showOverviewSummary && donutTotal > 0 && (
                <>
                  {hasPlanInfo && <span className="text-[#e2e8f0] dark:text-white/10 flex-shrink-0">|</span>}
                  <span className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[#64748b] dark:text-white/70 font-medium">Controls:</span>
                    <span className="font-bold text-[#0f172a] dark:text-white">{donutTotal}</span>
                  </span>
                  <span className="text-[#e2e8f0] dark:text-white/10 flex-shrink-0">|</span>
                  <span className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[#64748b] dark:text-white/70 font-medium">Satisfied:</span>
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">{tabPass}</span>
                  </span>
                  <span className="text-[#e2e8f0] dark:text-white/10 flex-shrink-0">|</span>
                  <span className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[#64748b] dark:text-white/70 font-medium">Not Satisfied:</span>
                    <span className={`font-bold ${tabFail > 0 ? 'text-red-600 dark:text-red-400' : 'text-[#64748b] dark:text-white/50'}`}>{tabFail}</span>
                  </span>
                  <span className="flex items-center gap-2 flex-1 min-w-0 ml-2 group/bar relative">
                    <div className="flex-1 flex h-1.5 rounded-full overflow-hidden bg-[#f1f5f9] dark:bg-white/[0.05] cursor-default">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-l-full transition-all duration-500" style={{ width: `${(tabPass / donutTotal) * 100}%` }} />
                      {tabFail > 0 && <div className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-r-full transition-all duration-500" style={{ width: `${(tabFail / donutTotal) * 100}%` }} />}
                    </div>
                    <span className={`text-[12px] font-extrabold tabular-nums flex-shrink-0 ${pctColor}`}>{pct}%</span>
                    {/* Tooltip on hover */}
                    <span className="absolute right-0 top-full mt-2 z-50 hidden group-hover/bar:block px-3 py-2.5 rounded-lg bg-[#0f172a] dark:bg-[#0f1420] text-[12px] text-white shadow-xl border border-white/10 whitespace-nowrap">
                      <span className="flex items-center gap-4 mb-1.5">
                        <span className="flex items-center gap-1.5"><span className="size-2 rounded-full bg-emerald-500" /><span className="font-medium">Satisfied: <span className="font-bold text-emerald-400">{tabPass}</span></span></span>
                        <span className="flex items-center gap-1.5"><span className="size-2 rounded-full bg-red-500" /><span className="font-medium">Not Satisfied: <span className="font-bold text-red-400">{tabFail}</span></span></span>
                      </span>
                      <span className="flex items-center gap-2 text-[11px] text-white/50">
                        <span>{tabPass} of {donutTotal} controls</span>
                        <span className="text-white/30">·</span>
                        <span className={`font-bold ${pctColor}`}>{pct}% compliance</span>
                      </span>
                      <span className="absolute -top-1 right-6 w-2 h-2 rotate-45 bg-[#0f172a] dark:bg-[#0f1420] border-l border-t border-white/10" />
                    </span>
                  </span>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* ─── Loading ─── */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <div className="size-14 rounded-2xl bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-violet-500/10 dark:to-violet-500/5 flex items-center justify-center shadow-sm">
            <Loader2 className="size-6 animate-spin text-indigo-500 dark:text-violet-400" />
          </div>
          <div className="text-center">
            <p className="text-[14px] font-semibold text-[#475569] dark:text-white/50">
              Loading results
            </p>
            <p className="text-[12px] text-[#64748b] dark:text-white/50 mt-1">
              Fetching assessment data
            </p>
          </div>
        </div>
      )}

      {/* ─── Error ─── */}
      {!isLoading && currentError && (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <p className="text-[14px] text-[#94a3b8] dark:text-white/70">Something went wrong.</p>
        </div>
      )}

      {/* ═══ NOT YET COMPLETE / NOTHING SUBMITTED BANNER ═══ */}
      {!isLoading && !currentError && (
        gateBlocked ||
        (reviewComplete === undefined && (
          (decData.length === 0 && autoStatuses.length === 0) ||
          (!decLoading && decData.length > 0 && !allDecFinalized)
        ))
      ) && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10 px-5 py-4 flex items-center gap-3">
          <AlertCircle className="size-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <div>
            <h3 className="text-[14px] font-semibold text-amber-800 dark:text-amber-300">
              {gateBlocked ? "Assessment not yet complete" : "Review Incomplete"}
            </h3>
            <p className="text-[13px] text-amber-700 dark:text-amber-400/80 mt-0.5">
              {gateBlocked
                ? "This plan is still in execution/review. Results become available once every statement of every control has been reviewed."
                : "Not all control statements have a final review decision. Results will be available once every control is approved or rejected, with no follow-ups pending."}
            </p>
          </div>
          <button
            onClick={() => navigate(`/assessment-review/${id}`)}
            className="ml-auto px-4 py-2 rounded-lg text-[12px] font-bold bg-amber-600 text-white hover:bg-amber-700 transition-colors flex-shrink-0"
          >
            Go to Review
          </button>
        </div>
      )}

      {/* ═══ MAIN CONTENT ═══ */}
      {!isLoading && !currentError && resultsReady && (
        <div className="space-y-6">
          {/* ── Search + Filters ── */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#64748b] dark:text-white/50" />
              <input
                type="text"
                placeholder="Search controls"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                className="w-full h-9 pl-9 pr-4 text-[13px] bg-white dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-violet-500/20 focus:border-indigo-500 dark:focus:border-violet-500/50 transition-all placeholder:text-[#64748b] dark:placeholder:text-white/60 text-[#0f172a] dark:text-white"
              />
            </div>

            {/* Status Tabs */}
            <div className="flex items-center h-9 gap-0.5 bg-[#f1f5f9] dark:bg-white/[0.04] rounded-xl px-1 border border-[#e2e8f0] dark:border-white/[0.07]">
              {([
                { key: "all" as ResultFilter, label: "All", count: tabAll },
                { key: "pass" as ResultFilter, label: "Satisfied", count: tabPass },
                { key: "fail" as ResultFilter, label: "Not Satisfied", count: tabFail },
              ]).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => { setResultFilter(opt.key); setPage(0); }}
                  className={`flex items-center gap-1.5 px-3 h-7 rounded-lg text-[12px] font-semibold transition-all ${
                    resultFilter === opt.key
                      ? "bg-indigo-600 dark:bg-violet-600 text-white shadow-sm"
                      : "text-[#334155] dark:text-white/80 hover:text-[#0f172a] dark:hover:text-white"
                  }`}
                >
                  {opt.label}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${
                    resultFilter === opt.key
                      ? "bg-white/20 text-white"
                      : "bg-[#e2e8f0] dark:bg-white/[0.12] text-[#334155] dark:text-white/80"
                  }`}>
                    {opt.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Method filter dropdown */}
            {(() => {
              const methodOptions = [
                { key: "all" as MethodFilter, label: "All Methods" },
                { key: "declarative" as MethodFilter, label: "Declarative" },
                { key: "declarative_evidence" as MethodFilter, label: "Declarative + Evidence" },
                { key: "automation" as MethodFilter, label: "Automation" },
              ];
              const selectedLabel = methodOptions.find((o) => o.key === methodFilter)?.label ?? "All Methods";
              const isFiltered = methodFilter !== "all";
              return (
                <div className="relative">
                  <button
                    onClick={() => setFilterOpen((v) => !v)}
                    className="flex items-center gap-2 h-9 px-3.5 rounded-xl text-[13px] font-semibold border transition-colors whitespace-nowrap bg-transparent text-[#1e293b] dark:text-white/85 border-[#e2e8f0] dark:border-white/[0.07] hover:bg-[#f8fafc] dark:hover:bg-white/[0.04] hover:text-[#0f172a] dark:hover:text-white"
                  >
                    <span>{selectedLabel}</span>
                    <svg className={`size-3.5 flex-shrink-0 transition-transform ${filterOpen ? "rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m6 9 6 6 6-6"/></svg>
                  </button>

                  {filterOpen && (
                    <>
                      <div className="fixed inset-0 z-[40]" onClick={() => setFilterOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 z-[41] min-w-[200px] w-full rounded-xl border border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-[#1e293b] shadow-xl overflow-hidden">
                        {methodOptions.map((opt) => (
                          <button
                            key={opt.key}
                            onClick={() => { setMethodFilter(opt.key); setPage(0); setFilterOpen(false); }}
                            className={`w-full text-left px-4 py-2.5 text-[13px] font-medium transition-colors whitespace-nowrap flex items-center justify-between gap-2 ${
                              methodFilter === opt.key
                                ? "bg-indigo-50 dark:bg-violet-500/10 text-indigo-700 dark:text-violet-400"
                                : "text-[#475569] dark:text-white/60 hover:bg-[#f8fafc] dark:hover:bg-white/[0.04]"
                            }`}
                          >
                            {opt.label}
                            {methodFilter === opt.key && (
                              <span className="size-1.5 rounded-full bg-indigo-500 dark:bg-violet-400 flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Rows per page (top, right-aligned) */}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-[13px] font-semibold text-[#0f172a] dark:text-white/90 whitespace-nowrap">Rows per page</span>
              <Dropdown
                value={String(rowsPerPage)}
                onChange={(v) => { setRowsPerPage(Number(v)); setPage(0); }}
                options={[10, 25, 100].map((n) => ({ value: String(n), label: String(n) }))}
                size="sm"
                align="end"
                className="!min-w-[80px]"
              />
            </div>
          </div>

          {/* ── Controls Table ── */}
          <div className="space-y-3">
            <div className="w-full overflow-hidden rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] shadow-sm">
              <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[700px] table-fixed text-[13px]">
                <thead>
                  <tr className="border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02]">
                    {(["index", "controlId", "description", "method", "status"] as const).map((key) => {
                      const labels: Record<string, string> = { index: "#", controlId: "Control Id", description: "Description", method: "Test Method", status: "Status" };
                      const widths: Record<string, string> = { index: "w-[5%]", controlId: "w-[12%]", description: "w-[43%]", method: "w-[20%]", status: "w-[20%]" };
                      const active = sortKey === key;
                      const isSortable = key !== "index";
                      // Description is a wrapped paragraph column — left-align its
                      // header to match the left-aligned text in its data cells
                      // below; every other sortable column is centered to match
                      // its centered badge/pill content.
                      const isLeftAlign = key === "description";
                      const icon = isSortable ? (
                        <span className="inline-flex flex-col items-center select-none shrink-0" style={{ fontSize: "7px", lineHeight: "7px" }}>
                          <span className={`font-black ${active && sortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}>▲</span>
                          <span className={`font-black ${active && sortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}>▼</span>
                        </span>
                      ) : null;
                      return (
                        <th key={key} onClick={isSortable ? () => handleSort(key) : undefined} className={`px-5 py-3.5 text-[13px] font-bold text-[#1e293b] dark:text-white/70 ${isLeftAlign ? "text-left" : "text-center"} ${widths[key]} ${isSortable ? "cursor-pointer select-none hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors" : ""} ${key !== "index" ? "border-l border-[#e2e8f0] dark:border-white/[0.06]" : ""}`}>
                          {!isSortable ? (
                            labels[key]
                          ) : isLeftAlign ? (
                            // Left align: the icon trails after the label without
                            // shifting where the label starts, so no spacer is needed.
                            <span className="inline-flex items-center gap-2 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                              {labels[key]}
                              {icon}
                            </span>
                          ) : (
                            // Center align: mirror the icon's width as an invisible
                            // spacer on the opposite side so the label sits at the
                            // column's true center instead of the label+icon group's
                            // center (which sits left of true center without this).
                            <span className="flex items-center w-full gap-2 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                              <span className="shrink-0 invisible" aria-hidden="true">{icon}</span>
                              <span className="flex-1 text-center">{labels[key]}</span>
                              <span className="shrink-0">{icon}</span>
                            </span>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-14 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="size-10 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] flex items-center justify-center">
                            <FileText className="size-4 text-[#64748b] dark:text-white/50" />
                          </div>
                          <p className="text-[13px] font-medium text-[#475569] dark:text-white/50">
                            No statements match your search
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pagedItems.map((unified, idx) => {
                      const meta = getItemMeta(unified);
                      const rowKey =
                        unified.kind === "automation"
                          ? `auto-${(unified.data as ControlExecutionStatusResponse).controlId}`
                          : `dec-group-${(unified.data as DecGroup).controlId}`;
                      const isSelected = drawerItem?.rowKey === rowKey;

                      return (
                        <tr
                          key={rowKey}
                          onClick={() =>
                            setDrawerItem({
                              kind: unified.kind,
                              data: unified.data,
                              rowKey,
                              meta,
                            })
                          }
                          className={`border-b border-[#e2e8f0] dark:border-white/[0.06] last:border-0 transition-colors cursor-pointer group
                          ${isSelected ? "bg-indigo-50/40 dark:bg-violet-500/[0.04]" : "hover:bg-indigo-50/50 dark:hover:bg-white/[0.05]"}`}
                        >
                          {/* # */}
                          <td className="px-5 py-4 text-center text-[#475569] dark:text-white/50 font-medium">
                            {page * rowsPerPage + idx + 1}
                          </td>
                          {/* Control Id */}
                          <td className="px-5 py-4 text-center border-l border-[#e2e8f0] dark:border-white/[0.06]">
                            <span className="text-xs font-mono font-bold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-2 py-0.5 rounded">
                              {meta.controlId?.toUpperCase()}
                            </span>
                          </td>
                          {/* Description */}
                          <td className="px-5 py-4 text-left border-l border-[#e2e8f0] dark:border-white/[0.06]">
                            <p className="font-medium text-[#0f172a] dark:text-white/80 text-[13px] break-words">
                              {meta.description || "—"}
                            </p>
                          </td>
                          {/* Checks */}
                          {/* <td className="px-5 py-4 text-center border-l border-[#e2e8f0] dark:border-white/[0.06]">
                          {meta.checks > 0 && (
                            <span className="text-[12px] font-medium text-[#cbd5e1] dark:text-white/70 px-2.5 py-1 rounded-full bg-[#f1f5f9] dark:bg-white/[0.04]">
                              {meta.checks} check{meta.checks !== 1 ? 's' : ''}
                            </span>
                          )}
                        </td> */}
                          {/* Test Method */}
                          <td className="px-5 py-4 text-center border-l border-[#e2e8f0] dark:border-white/[0.06]">
                            <span
                              className={`inline-flex items-center justify-center w-[160px] py-1.5 rounded-full text-[12px] font-bold ${
                                meta.method === "Automation"
                                  ? "bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border border-cyan-200/60 dark:border-cyan-500/15"
                                  : meta.method === "Declarative + Evidence"
                                    ? "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 border border-sky-200/60 dark:border-sky-500/15"
                                    : "bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 border border-violet-200/60 dark:border-violet-500/20"
                              }`}
                            >
                              {meta.method}
                            </span>
                          </td>
                          {/* Status */}
                          <td className="px-5 py-4 text-center border-l border-[#e2e8f0] dark:border-white/[0.06]">
                            <span
                              className={`inline-flex items-center justify-center gap-1.5 w-[140px] py-1.5 rounded-full text-[12px] font-bold ${
                                meta.noData
                                  ? "bg-slate-50 dark:bg-white/[0.04] text-slate-500 dark:text-white/40"
                                  : meta.passed
                                    ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                    : "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400"
                              }`}
                            >
                              {meta.noData ? (
                                <AlertTriangle className="size-3.5" />
                              ) : meta.passed ? (
                                <CheckCircle2 className="size-3.5" />
                              ) : (
                                <XCircle className="size-3.5" />
                              )}
                              {meta.noData ? "No Data" : meta.passed ? "Satisfied" : "Not Satisfied"}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              </div>
            </div>

            {/* ── Right-side Slide-out Drawer ── */}
            {drawerItem &&
              (() => {
                const { kind, data, meta } = drawerItem;
                return (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-[50] bg-black/30 backdrop-blur-[2px] transition-opacity"
                      onClick={() => setDrawerItem(null)}
                    />
                    {/* Drawer panel */}
                    <div className="fixed top-0 right-0 z-[51] h-full w-[960px] max-w-[96vw] bg-white dark:bg-[#131320] border-l border-[#e2e8f0] dark:border-white/[0.08] shadow-2xl flex flex-col animate-[slideInRight_0.25s_ease-out]">
                      {/* Header */}
                      <div className="flex items-start justify-between px-6 py-4 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02]">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <span className="text-xs font-mono font-bold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-2.5 py-1 rounded-lg flex-shrink-0 mt-0.5">
                            {meta.controlId?.toUpperCase()}
                          </span>
                          <span className="text-[14px] font-medium text-[#0f172a] dark:text-white break-words leading-snug">
                            {meta.description || "Control Details"}
                          </span>
                        </div>
                        <button
                          onClick={() => setDrawerItem(null)}
                          className="p-2 rounded-lg hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors flex-shrink-0"
                        >
                          <X className="size-4 text-[#64748b] dark:text-white/50" />
                        </button>
                      </div>

                      {/* Status bar */}
                      <div className="flex items-center gap-3 px-6 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06]">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold ${
                            meta.noData
                              ? "bg-slate-50 dark:bg-white/[0.04] text-slate-500 dark:text-white/40"
                              : meta.passed
                                ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                : "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400"
                          }`}
                        >
                          {meta.noData ? (
                            <AlertTriangle className="size-3.5" />
                          ) : meta.passed ? (
                            <CheckCircle2 className="size-3.5" />
                          ) : (
                            <XCircle className="size-3.5" />
                          )}
                          {meta.noData ? "No Data" : meta.passed ? "Satisfied" : "Not Satisfied"}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold ${
                            meta.method === "Automation"
                              ? "bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border border-cyan-200/60 dark:border-cyan-500/15"
                              : "bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 border border-violet-200/60 dark:border-violet-500/20"
                          }`}
                        >
                          {meta.method === "Automation" ? (
                            <Zap className="size-3" />
                          ) : (
                            <FileText className="size-3" />
                          )}
                          {meta.method}
                        </span>
                        {/* {meta.checks > 0 && (
                          <span className="text-[12px] font-medium text-[#cbd5e1] dark:text-white/70 px-2.5 py-1 rounded-full bg-[#f1f5f9] dark:bg-white/[0.04]">
                            {meta.checks} check{meta.checks !== 1 ? "s" : ""}
                          </span>
                        )} */}
                      </div>

                      {/* Scrollable content */}
                      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                        {/* Declarative group content */}
                        {kind === "declarative_group" && (
                          <div className="space-y-3">
                            {(data as DecGroup).items.map((item) => {
                              const exec = item.execution;
                              // Prefer the most recent evidence createdAt (from the
                              // evidence module API); fall back to execution time.
                              const atTimestamp =
                                (evidenceMap[exec.statementId] ?? []).reduce<
                                  string | null
                                >(
                                  (latest, ev) =>
                                    !latest ||
                                    new Date(ev.createdAt) > new Date(latest)
                                      ? ev.createdAt
                                      : latest,
                                  null,
                                ) ?? exec.executedAt;
                              return (
                                <div
                                  key={exec.id}
                                  className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden"
                                >
                                  <div className="flex items-center gap-3 px-4 py-3">
                                    <div className="w-1 self-stretch rounded-full bg-indigo-400 dark:bg-violet-500 flex-shrink-0" />
                                    <span className="flex-shrink-0 inline-flex items-center gap-1.5 text-[12px] font-mono px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-violet-500/10">
                                      <span className="font-bold text-indigo-700 dark:text-violet-400">
                                        {exec.statementId?.toUpperCase()}
                                      </span>
                                    </span>
                                    <span className="flex-1" />
                                    {(() => {
                                      const msgCount = countVisibleChatMessages(item.reviewHistory ?? []);
                                      if (msgCount === 0) return null;
                                      return (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setChatItem(item);
                                            setChatOpen(true);
                                          }}
                                          className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-indigo-50 dark:bg-violet-500/10 text-indigo-600 dark:text-violet-400 hover:bg-indigo-100 dark:hover:bg-violet-500/20 transition-colors border border-indigo-200/60 dark:border-violet-500/15 mr-2"
                                          title="View chat history"
                                        >
                                          <MessageSquare className="size-3" />
                                          Chat
                                          <span className="size-4 rounded-full bg-indigo-600 dark:bg-violet-600 text-white text-[9px] flex items-center justify-center">
                                            {msgCount}
                                          </span>
                                        </button>
                                      );
                                    })()}
                                    {(() => {
                                      const execStmt = execStatementByKey.get(exec.statementId);
                                      const stmtSatisfied = execStmt?.review_status != null
                                        ? isAcceptedReview(execStmt.review_status)
                                        : resolveDecDetermination(exec.controlId, exec.determination);
                                      return (
                                        <span
                                          className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-bold ${
                                            stmtSatisfied
                                              ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                              : "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400"
                                          }`}
                                        >
                                          {stmtSatisfied ? (
                                            <CheckCircle2 className="size-3" />
                                          ) : (
                                            <XCircle className="size-3" />
                                          )}
                                          {stmtSatisfied ? "Satisfied" : "Not Satisfied"}
                                        </span>
                                      );
                                    })()}
                                  </div>

                                  <div className="px-4 pb-4 pt-2 border-t border-[#e2e8f0] dark:border-white/[0.06]">
                                    <div className="ml-4 space-y-3">
                                      {exec.statementDescription && (
                                        <p className="text-[12px] text-[#1e293b] dark:text-white/85 leading-relaxed">
                                          {exec.statementDescription}
                                        </p>
                                      )}
                                      <div className="rounded-xl bg-[#f8fafc] dark:bg-white/[0.02] border border-[#e2e8f0]/60 dark:border-white/[0.04] p-4">
                                        <p className="text-[12px] font-semibold text-[#334155] dark:text-white/85 mb-2">
                                          Justification
                                        </p>
                                        <p className="text-[13px] text-[#0f172a] dark:text-white/90 leading-relaxed">
                                          {exec.justification}
                                        </p>
                                      </div>
                                      {renderReviewerDecision(execStatementByKey.get(exec.statementId))}
                                      <div className="flex flex-wrap gap-2">
                                        <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#f8fafc] dark:bg-white/[0.02] border border-[#e2e8f0]/60 dark:border-white/[0.04]">
                                          <Clock className="size-3 text-indigo-500 dark:text-violet-300" />
                                          <span className="text-[12px] font-medium text-[#334155] dark:text-white/85">
                                            At
                                          </span>
                                          <span className="text-[12px] font-semibold text-[#0f172a] dark:text-white/90">
                                            {atTimestamp
                                              ? formatDateUtc(atTimestamp)
                                              : "-"}
                                          </span>
                                        </div>
                                        <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border ${
                                          hasEvidence(exec.statementId)
                                            ? "bg-sky-50 dark:bg-sky-500/10 border-sky-200/60 dark:border-sky-500/15"
                                            : "bg-[#f8fafc] dark:bg-white/[0.02] border-[#e2e8f0]/60 dark:border-white/[0.04]"
                                        }`}>
                                          <FileText className={`size-3 ${hasEvidence(exec.statementId) ? "text-sky-500 dark:text-sky-300" : "text-indigo-500 dark:text-violet-300"}`} />
                                          <span className={`text-[12px] font-semibold ${hasEvidence(exec.statementId) ? "text-sky-700 dark:text-sky-300" : "text-[#0f172a] dark:text-white/90"}`}>
                                            {hasEvidence(exec.statementId)
                                              ? "Declarative + Evidence"
                                              : "Declarative"}
                                          </span>
                                        </div>
                                      </div>
                                      {hasEvidence(exec.statementId) && (
                                        <div className="rounded-xl bg-[#f8fafc] dark:bg-white/[0.02] border border-[#e2e8f0]/60 dark:border-white/[0.04] p-4">
                                          <p className="text-[12px] font-semibold text-[#334155] dark:text-white/85 mb-2">
                                            Attached Evidence
                                          </p>
                                          <div className="space-y-1.5">
                                            {(
                                              evidenceMap[exec.statementId] ??
                                              []
                                            ).map((ev) => {
                                              const evidenceLabel =
                                                ev.evidenceName ||
                                                `Evidence ${ev.supportingEvidenceId.slice(0, 8)}`;

                                              return (
                                                <div
                                                  key={ev.id}
                                                  className="flex items-center justify-between gap-2 p-2 rounded-md bg-white dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.06]"
                                                >
                                                  <div className="flex items-start gap-2 min-w-0 flex-1 group/evidence relative">
                                                    <FileText className="size-3.5 text-indigo-500 dark:text-violet-400 flex-shrink-0" />
                                                    <p
                                                      className="text-[12px] font-medium text-[#0f172a] dark:text-white/80 line-clamp-1 cursor-default"
                                                    >
                                                      {evidenceLabel}
                                                    </p>
                                                    {evidenceLabel && (
                                                      <span className="absolute left-0 bottom-full mb-2 z-50 hidden group-hover/evidence:block px-3 py-2 rounded-lg bg-[#0f172a] dark:bg-[#0f1420] text-[12px] text-white font-medium shadow-xl border border-white/10 whitespace-nowrap">
                                                        {evidenceLabel}
                                                        <span className="absolute -bottom-1 left-4 w-2 h-2 rotate-45 bg-[#0f172a] dark:bg-[#0f1420] border-r border-b border-white/10" />
                                                      </span>
                                                    )}
                                                  </div>
                                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEvidencePreview(
                                                          ev.supportingEvidenceId,
                                                          evidenceLabel,
                                                        );
                                                      }}
                                                      className="flex items-center gap-1 px-2 py-1 rounded text-[12px] font-bold text-indigo-700 dark:text-violet-300 bg-indigo-50 dark:bg-violet-500/10 hover:bg-indigo-100 dark:hover:bg-violet-500/20 transition-colors"
                                                    >
                                                      <Eye className="size-3" />{" "}
                                                      Preview
                                                    </button>
                                                    <a
                                                      href={downloadSupportingEvidenceFile(
                                                        ev.supportingEvidenceId,
                                                      )}
                                                      download
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      className="flex items-center gap-1 px-2 py-1 rounded text-[12px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors"
                                                    >
                                                      <Download className="size-3" />{" "}
                                                      Download
                                                    </a>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                      {exec.followUpQuestion && (
                                        <div className="rounded-xl bg-orange-50/70 dark:bg-orange-500/[0.06] border border-orange-200 dark:border-orange-500/10 p-4">
                                          <div className="flex items-center gap-1.5 mb-2">
                                            <MessageSquare className="size-3.5 text-orange-500" />
                                            <p className="text-[12px] font-medium text-orange-600 dark:text-orange-400">
                                              Follow-Up Question
                                            </p>
                                          </div>
                                          <p className="text-[13px] text-orange-800 dark:text-orange-300 leading-relaxed">
                                            {exec.followUpQuestion}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Automation content */}
                        {kind === "automation" &&
                          (() => {
                            const ctrl = data as ControlExecutionStatusResponse;
                            const opaResults = (() => {
                              const seen = new Set<string>();
                              return (ctrl.opaResults ?? []).filter((opa) => {
                                const key = `${opa.opaControlId}_${opa.assetUuid ?? ''}`;
                                if (seen.has(key)) return false;
                                seen.add(key);
                                return true;
                              });
                            })();
                            const execCtrl = execControlByControlId.get(ctrl.controlId);
                            const rollup = isControlSatisfiedById(ctrl.controlId);
                            const ctrlPassed = rollup != null ? rollup : ctrl.opaOverallStatus === 'passed';
                            const ctrlNoData = rollup == null && ctrl.opaOverallStatus == null && opaResults.length === 0;
                            const execStmtsWithAssets = (execCtrl?.statements ?? []).filter(
                              (s) => (s.assets?.length ?? 0) > 0 || s.review_status != null,
                            );

                            return (
                              <div className="space-y-4">
                                {ctrl.statements?.[0]?.statementDescription && (
                                  <div className="rounded-xl bg-[#f8fafc] dark:bg-white/[0.02] border border-[#e2e8f0]/60 dark:border-white/[0.04] p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <p className="text-[12px] font-semibold text-[#334155] dark:text-white/85">
                                        Control Statement
                                      </p>
                                      {ctrl.statements?.[0]?.statementId && (
                                        <span className="text-[12px] font-mono font-bold text-indigo-600 dark:text-violet-300 bg-indigo-50 dark:bg-violet-500/15 px-2 py-0.5 rounded">
                                          {ctrl.statements[0].statementId?.toUpperCase()}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[13px] text-[#0f172a] dark:text-white/90 leading-relaxed">
                                      {ctrl.statements[0].statementDescription}
                                    </p>
                                  </div>
                                )}

                                {/* Execution-view snapshot: reviewer decision + frozen asset matrix */}
                                {execStmtsWithAssets.length > 0 &&
                                  execStmtsWithAssets.map((s) => (
                                    <div key={s.statement_id} className="space-y-3">
                                      {execCtrl && execCtrl.statements.length > 1 && (
                                        <span className="inline-flex items-center text-[12px] font-mono font-bold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-2 py-0.5 rounded">
                                          {(s.statement_label || s.statement_id)?.toUpperCase()}
                                        </span>
                                      )}
                                      {renderReviewerDecision(s)}
                                      {renderAssetMatrix(s)}
                                    </div>
                                  ))}

                                {/* OPA Results — legacy fallback when execution-view has no snapshot */}
                                {execStmtsWithAssets.length === 0 && opaResults.map((opa, opaIdx) => {
                                  const opaPassed = opa.controlStatus === 'passed';
                                  return (
                                    <div key={opaIdx} className={`rounded-lg border p-3 space-y-2 ${
                                      opaPassed
                                        ? "border-emerald-100 dark:border-emerald-500/10 bg-emerald-50/30 dark:bg-emerald-500/[0.03]"
                                        : "border-red-100 dark:border-red-500/10 bg-red-50/30 dark:bg-red-500/[0.03]"
                                    }`}>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[12px] font-bold ${
                                          opaPassed
                                            ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                                            : "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400"
                                        }`}>
                                          {opaPassed ? <CheckCircle2 className="size-3" /> : <XCircle className="size-3" />}
                                          {opaPassed ? "Satisfied" : "Not Satisfied"}
                                        </span>
                                        {opa.catalogName && (
                                          <span className="text-[12px] text-[#334155] dark:text-white/85 bg-[#f1f5f9] dark:bg-white/[0.06] px-2 py-0.5 rounded">
                                            {opa.catalogName}
                                          </span>
                                        )}
                                      </div>
                                      {opa.assetName && (
                                        <div className="flex items-center gap-2 text-[12px] text-[#1e293b] dark:text-white/85">
                                          <span className="font-semibold text-[#334155] dark:text-white/85">Asset:</span>
                                          <span className="font-mono text-[#0f172a] dark:text-white">{opa.assetName}</span>
                                          {opa.assetIdentifierType && (
                                            <span className="text-[#475569] dark:text-white/70">({opa.assetIdentifierType})</span>
                                          )}
                                        </div>
                                      )}
                                      {(opa.evidence ?? []).length > 0 && (
                                        <div className="mt-2">
                                          <p className={`text-[12px] font-black tracking-wide mb-1.5 ${opaPassed ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                                            Evidence ({opa.evidence.length})
                                          </p>
                                          <div className={`rounded-lg border bg-[#f8f9fa] dark:bg-[#1e1e1e] overflow-hidden ${opaPassed ? "border-emerald-200 dark:border-emerald-500/20" : "border-red-200 dark:border-red-500/20"}`}>
                                            <div className="flex items-center justify-between px-3 py-1.5 bg-[#e9ecef] dark:bg-[#2d2d2d] border-b border-[#dee2e6] dark:border-[#404040]">
                                              <span className="text-[10px] text-[#6c757d] dark:text-[#858585] font-mono">evidence.json</span>
                                              <span className="text-[10px] text-[#858585]">{opa.evidence.length} items</span>
                                            </div>
                                            <pre className="px-3 py-2.5 text-[11px] text-[#212529] dark:text-[#d4d4d4] overflow-auto max-h-[500px] font-mono leading-relaxed m-0 whitespace-pre-wrap break-words">
{JSON.stringify(opa.evidence, null, 2)}
                                            </pre>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}

                              </div>
                            );
                          })()}
                      </div>
                    </div>
                  </>
                );
              })()}

            {/* Pagination */}
            {currentItems.length > 0 && (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-[13px] text-[#334155] dark:text-white/85">
                  Showing{" "}
                  <span className="font-bold text-[#0f172a] dark:text-white">
                    {page * rowsPerPage + 1}–
                    {Math.min((page + 1) * rowsPerPage, currentItems.length)}
                  </span>{" "}
                  of{" "}
                  <span className="font-bold text-[#0f172a] dark:text-white">
                    {currentItems.length}
                  </span>{" "}
                  controls
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                      className="p-1.5 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="size-3.5" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setPage(i)}
                        className={`w-8 h-8 text-[13px] font-medium rounded-lg transition-colors ${
                          i === page
                            ? "bg-indigo-600 dark:bg-violet-600 text-white"
                            : "text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                      className="p-1.5 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="size-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      )}

      {/* ── Re-Compile Confirmation Modal ── */}
      {compileConfirm && (
        <ModalShell open onClose={() => setCompileConfirm(false)} zIndexClass="z-[60]">
          <div className="relative p-6 space-y-4 overflow-y-auto flex-1">
            <button
              onClick={() => setCompileConfirm(false)}
              className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
            >
              <X className="size-4 text-[#64748b] dark:text-white/40" />
            </button>
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="size-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-[15px] font-medium text-[#0f172a] dark:text-white">
                  Re-Compile Results?
                </h3>
                <p className="text-[12px] text-[#64748b] dark:text-white/50 mt-0.5">
                  This will delete existing compiled results and create fresh
                  ones. POA&M items may be recreated.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setCompileConfirm(false)}
                className="px-4 py-2 rounded-lg text-[12px] font-medium text-[#cbd5e1] dark:text-white/70 bg-[#f1f5f9] dark:bg-white/[0.05] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCompile}
                className="px-4 py-2 rounded-lg text-[12px] font-bold text-white bg-amber-600 hover:bg-amber-700 transition-colors"
              >
                Yes, Re-Compile
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {/* ── Compile Success Modal ── */}
      {compileSuccess && (
        <ModalShell open onClose={() => setCompileSuccess(false)} zIndexClass="z-[60]">
          <div className="relative p-6 text-center space-y-4 overflow-y-auto flex-1 flex flex-col items-center justify-center">
            <button
              onClick={() => setCompileSuccess(false)}
              className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
            >
              <X className="size-4 text-[#64748b] dark:text-white/40" />
            </button>
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center">
              <CheckCircle2 className="size-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-lg font-bold text-[#0f172a] dark:text-white">
              Results Compiled Successfully
            </h3>
            <p className="text-sm text-[#64748b] dark:text-white/50">
              Your assessment results have been compiled
              {poamItems.length > 0
                ? ` and ${poamItems.length} POA&M item${poamItems.length !== 1 ? "s have" : " has"} been generated`
                : ""}
              .
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setCompileSuccess(false)}
                className="px-5 py-2.5 rounded-xl text-[13px] font-medium text-[#cbd5e1] dark:text-white/70 bg-[#f1f5f9] dark:bg-white/[0.05] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] transition-colors"
              >
                Close
              </button>
              {poamId && (
                <button
                  onClick={() => {
                    setCompileSuccess(false);
                    navigate(`/poam/${poamId}`);
                  }}
                  className="px-5 py-2.5 rounded-xl text-[13px] font-bold bg-indigo-600 dark:bg-violet-600 text-white hover:bg-indigo-700 dark:hover:bg-violet-700 transition-all shadow-sm flex items-center gap-1.5"
                >
                  <ExternalLink className="size-3.5" />
                  View POA&M
                </button>
              )}
            </div>
          </div>
        </ModalShell>
      )}

      {/* ── Chat History Modal (read-only) ── */}
      {chatOpen && chatItem && (
        <FollowUpChatModal
          open={chatOpen}
          onClose={() => { setChatOpen(false); setChatItem(null); }}
          statementId={chatItem.execution.statementId}
          controlId={chatItem.execution.controlId}
          reviewHistory={chatItem.reviewHistory}
          currentFollowUpQuestion={chatItem.execution.followUpQuestion}
          currentStatus={chatItem.currentReviewStatus}
          perspective="review"
        />
      )}

      {/* ── Evidence Preview Modal ── */}
      {(previewUrl || previewLoading) && (
        <ModalShell open onClose={closePreview} zIndexClass="z-[60]" variant="preview">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06] flex-shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="size-4 text-indigo-500 dark:text-violet-400" />
                <span className="text-sm font-semibold text-[#0f172a] dark:text-white">
                  {previewTitle}
                </span>
              </div>
              <button
                onClick={closePreview}
                className="p-1.5 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
              >
                <X className="size-4 text-[#64748b] dark:text-white/50" />
              </button>
            </div>
            <div className="flex-1 min-h-0 p-1">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="size-8 animate-spin text-indigo-500 dark:text-violet-400" />
                </div>
              ) : previewUrl ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-full rounded border-0"
                  title="Evidence Preview"
                />
              ) : null}
            </div>
        </ModalShell>
      )}

      {/* ── Command + Evidence JSON Modal ── */}
      {jsonModal && (
        <ModalShell open onClose={() => setJsonModal(null)} zIndexClass="z-[60]" variant="wide">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#e2e8f0] dark:border-white/[0.06] flex-shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="flex items-center justify-center size-7 rounded-lg bg-indigo-50 dark:bg-violet-500/10 text-indigo-600 dark:text-violet-300 flex-shrink-0">
                  <Braces className="size-4" />
                </span>
                <div className="min-w-0">
                  <p className="font-mono text-[13px] font-bold text-[#0f172a] dark:text-white truncate">
                    {jsonModal.techControlId?.toUpperCase()}
                    <span className="ml-1.5 font-sans text-[#64748b] dark:text-white/40">/ {jsonModal.label}</span>
                  </p>
                  {(jsonModal.hostname || jsonModal.ipAddress) && (
                    <p className="text-[11px] text-[#64748b] dark:text-white/40 truncate">
                      {jsonModal.hostname}
                      {jsonModal.ipAddress ? ` (${jsonModal.ipAddress})` : ""}
                    </p>
                  )}
                </div>
                <span
                  className={`ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold flex-shrink-0 ${
                    jsonModal.status === "passed"
                      ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                      : jsonModal.status === "failed"
                        ? "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400"
                        : "bg-slate-100 dark:bg-white/[0.06] text-slate-600 dark:text-white/40"
                  }`}
                >
                  {jsonModal.status}
                </span>
              </div>
              <button
                onClick={() => setJsonModal(null)}
                className="p-1.5 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors flex-shrink-0"
              >
                <X className="size-4 text-[#64748b] dark:text-white/50" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-4">
              {(() => {
                const payload = {
                  tech_control_id: jsonModal.techControlId,
                  status: jsonModal.status,
                  asset: {
                    hostname: jsonModal.hostname ?? null,
                    ip_address: jsonModal.ipAddress ?? null,
                  },
                  [jsonModal.field]: jsonModal.content,
                };
                const json = JSON.stringify(payload, null, 2);
                return (
                  <div className="rounded-lg border border-[#e2e8f0] dark:border-[#2d2d2d] bg-[#f8f9fa] dark:bg-[#1e1e1e] overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-1.5 bg-[#e9ecef] dark:bg-[#2d2d2d] border-b border-[#dee2e6] dark:border-[#404040]">
                      <span className="text-[10px] font-mono text-[#6c757d] dark:text-[#858585]">
                        {jsonModal.field}.json
                      </span>
                      <button
                        onClick={() => {
                          navigator.clipboard?.writeText(json);
                          useToastStore.getState().showSuccess("Copied", "JSON copied to clipboard");
                        }}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#6c757d] dark:text-[#a0a0a0] hover:text-indigo-600 dark:hover:text-violet-300 transition-colors"
                      >
                        <ClipboardList className="size-3" /> Copy
                      </button>
                    </div>
                    <pre className="px-4 py-3 text-[14px] text-[#1a1a1a] dark:text-[#f0f0f0] overflow-auto max-h-[74vh] font-mono leading-relaxed m-0 whitespace-pre-wrap break-words">
{json}
                    </pre>
                  </div>
                );
              })()}
            </div>
        </ModalShell>
      )}
    </PageShell>
  );
}
